import React, { SyntheticEvent, useEffect, useState } from 'react'
import {
    Box,
    Grid,
    Card,
    Divider,
    CardContent,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    FormHelperText,
    Stack,
    Fab
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import moment from 'moment'
import Autocomplete from '@mui/material/Autocomplete'
import axios from 'axios'
import Swal from 'sweetalert2'

import apiUrl from '../../../../configs/constant';
import MsiDatagrid from '../../../../components/custom/MsiDatagridAuto';
import { useNavigate } from 'react-router-dom'
import Icon from '../../../../components/icon';
import { useToast } from '../../../../components/ToastProvider'

/**
 * DialogAddRow
 * props:
 *  - open
 *  - onClose
 *  - onSubmit({ nomor_akun, profit_center, keterangan, total, type }) // type = 'debet' | 'kredit'
 *  - typeDefault 'debet'|'kredit'
 */
const formatNumber = (value: string) => {
    const raw = value.replace(/[^0-9,]/g, '')
    const parts = raw.split(',')
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    return parts.join(',')
}

const DialogAddRow = ({ open, onClose, onSubmit, typeDefault = 'debet' }: any) => {
    const { showToast, hideToast } = useToast();
    const userData = JSON.parse(localStorage.getItem('user')!)
    const token = userData.token

    const [valAkun, setValAkun] = useState<any>(null)
    const [listAkun, setListAkun] = useState<any[]>([])
    const [listProfitCenter, setListProfitCenter] = useState<any[]>([])
    const [profitCenter, setProfitCenter] = useState<any>(null)

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors }
    } = useForm({
        defaultValues: {
            id: '',
            nomor_akun: '',
            nama_akun: '',
            nama_profit_center: '',
            profit_center: '',
            keterangan: '',
            total: ''
        }
    })

    const getAkun = () => {
        const url = apiUrl.apiUrl + '/acc/mutasi-kas/get-akun'
        axios
            .get(url, {
                params: {},
                headers: { Authorization: `Bearer ${token}` }
            })
            .then(response => {
                if (response.data.success) {
                    const data = response.data.data
                    if (Array.isArray(data)) {
                        data.forEach((item: any) => {
                            if (item && item.id != null && item.nama != null) item.nama = `${item.id} | ${item.nama}`
                        })
                    }
                    setListAkun(data)
                } else {
                    showToast(response.data.message, 'error');
                }
            })
    }
    const getListProfitCenter = () => {
        let url = apiUrl.apiUrl + '/acc/penyesuaian/list-profit-center'

        axios
            .get(url, {
                params: {},
                headers: { Authorization: `Bearer ${token}` }
            })
            .then(response => {
                const data = response.data.data
                setListProfitCenter(data)
            })
    }

    const handleChangeAkun = (event: SyntheticEvent, newValue: any | null) => {
        setValue('nomor_akun', newValue?.id)
        setValue('nama_akun', newValue?.nama)
        setValAkun(newValue)
    }

    const handleChangeProfitCenter = (event: SyntheticEvent, newValue: any | null) => {
        setValue('profit_center', newValue?.id)
        setValue('nama_profit_center', newValue?.nama_profitcenter)
        setProfitCenter(newValue)
    }

    useEffect(() => {
        getAkun()
        getListProfitCenter()
    }, [])

    useEffect(() => {
        if (!open) {
            reset()
            setValAkun(null)
            setProfitCenter(null)
        }
    }, [open])

    const submit = (data: any) => {
        // protect: ensure numeric
        const totalNum = Number(String(data.total || '0').replace(/[^0-9.-]+/g, '')) || 0
        onSubmit({ ...data, total: totalNum, type: typeDefault })
        reset()
    }

    return (
        <Dialog open={open} fullWidth maxWidth='sm' onClose={onClose}>
            <DialogTitle>
                <Typography sx={{ fontWeight: 500, mb: 1.5, color: '#212121' }}>
                    Tambah {typeDefault === 'debet' ? 'Debet' : 'Kredit'}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <Typography variant='body1' sx={{ fontWeight: 'bold', mb: 1.5 }}>
                            Akun
                        </Typography>
                        <Controller
                            name='nomor_akun'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <Autocomplete
                                    value={valAkun}
                                    options={listAkun}
                                    id='nomor_akun'
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    onChange={handleChangeAkun}
                                    renderOption={(props, option) => {
                                        return (
                                            <li {...props} key={option.id}>
                                                {option.nama}
                                            </li>
                                        )
                                    }}
                                    getOptionLabel={option => option.nama}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label='Akun'
                                            error={Boolean(errors.nomor_akun)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                />
                            )}
                        />
                        {errors.nomor_akun && (
                            <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography variant='body1' sx={{ fontWeight: 'bold', mb: 1.5 }}>
                            Profit Center
                        </Typography>
                        <FormControl fullWidth>
                            <Autocomplete
                                value={profitCenter}
                                options={listProfitCenter}
                                id='profitCenter'
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={handleChangeProfitCenter}
                                renderOption={(props, option) => {
                                    return (
                                        <li {...props} key={option.id}>
                                            {option.kode} - {option.nama_profitcenter}
                                        </li>
                                    )
                                }}
                                getOptionLabel={option => option.nama_profitcenter}
                                renderInput={params => <TextField {...params} label='Profit Center' />}
                            />
                            {errors.profit_center && (
                                <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography variant='body1' sx={{ fontWeight: 'bold', mb: 1.5 }}>
                            Total
                        </Typography>
                        <Controller
                            name="total"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, value, ...field } }) => (
                                <TextField
                                    {...field}
                                    value={formatNumber(value?.toString() || '')}
                                    label="Total"
                                    fullWidth
                                    inputProps={{ inputMode: 'numeric' }}
                                    onChange={(e) => {
                                        const rawValue = e.target.value.replace(/\./g, '')
                                        onChange(rawValue) // simpan tanpa titik
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant='body1' sx={{ fontWeight: 'bold', mb: 1.5 }}>
                            Keterangan
                        </Typography>
                        <Controller
                            name='keterangan'
                            control={control}
                            render={({ field }) => <TextField {...field} label='Keterangan' fullWidth multiline rows={2} />}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button color='primary' onClick={onClose}>
                    Batal
                </Button>
                <Button variant='contained' onClick={handleSubmit(submit)}>
                    Tambah
                </Button>
            </DialogActions>
        </Dialog>
    )
}

/**
 * AddMemorialSingleTable
 * main component
 */
const AddMemorialSingleTable: React.FC = () => {
    const { showToast, hideToast } = useToast();
    const navigate = useNavigate();

    const userData = JSON.parse(localStorage.getItem('user')!)
    const token = userData.token

    // master form
    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors }
    } = useForm({
        defaultValues: {
            sysdoctag_id: '',
            tgl: moment().format('YYYY-MM-DD'),
            keterangan: ''
        }
    })

    const handleChangeDoctag = (event: SyntheticEvent, newValue: any | null) => {
        setValue('sysdoctag_id', newValue?.id)
        setValDoctag(newValue)
    }

    // rows single table: each row has { id, nomor_akun, profit_center, keterangan, debet, kredit }
    const [rows, setRows] = useState<any[]>([])
    const [valDoctag, setValDoctag] = useState<any>(null)
    const [listDoctag, setListDoctag] = useState<any[]>([])

    // dialog control
    const [openDialog, setOpenDialog] = useState(false)
    const [dialogType, setDialogType] = useState<'debet' | 'kredit'>('debet')

    // totals
    const [totalDebet, setTotalDebet] = useState<number>(0)
    const [totalKredit, setTotalKredit] = useState<number>(0)

    const getDoctag = () => {
        const url = apiUrl.apiUrl + '/acc/penyesuaian/get-doctag'
        axios
            .get(url, {
                params: {},
                headers: { Authorization: `Bearer ${token}` }
            })
            .then(response => {
                if (response.data.success) {
                    const data = response.data.data
                    setListDoctag(data)
                } else {
                    showToast(response.data.message, 'error');
                }
            })
    }

    useEffect(() => {
        getDoctag()
    }, [])

    // calculate totals when rows change
    useEffect(() => {
        const sumD = rows.reduce((acc, r) => acc + (Number(r.debet) || 0), 0)
        const sumK = rows.reduce((acc, r) => acc + (Number(r.kredit) || 0), 0)
        setTotalDebet(sumD)
        setTotalKredit(sumK)
    }, [rows])

    const isBalance = totalDebet === totalKredit && totalDebet > 0

    // convertComponent untuk TableGrid: format Debet/Kredit and Action
    const convertComponent = [
        {
            key: 'debet',
            renderCell: (params: any) => (
                <Typography variant='body2'>{Number(params.data.debet || 0).toLocaleString('id-ID')}</Typography>
            )
        },
        {
            key: 'kredit',
            renderCell: (params: any) => (
                <Typography variant='body2'>{Number(params.data.kredit || 0).toLocaleString('id-ID')}</Typography>
            )
        },
        {
            key: 'action',
            renderCell: (params: any) => (
                <Button
                    size='small'
                    color='error'
                    onClick={() => {
                        setRows(prev => prev.filter(r => r.id !== params.data.id))
                    }}
                >
                    <Icon icon='mdi:trash-can-outline' />
                </Button>
            )
        }
    ]

    // add new row coming from dialog
    const handleAddRow = (data: any) => {
        // cek duplikasi nomor_akun
        const isDuplicate = rows.some(r => r.nomor_akun === data.nomor_akun)

        if (isDuplicate) {
            Swal.fire({
                icon: 'warning',
                title: 'Nomor Akun Sudah Ada',
                text: `Nomor akun ${data.nomor_akun} sudah ditambahkan sebelumnya`,
                confirmButtonColor: '#3085d6'
            })
            return // hentikan proses tambah row
        }

        const newRow = {
            id: Math.random().toString(36).substr(2, 9),
            nomor_akun: data.nomor_akun || '',
            nama_akun: data.nama_akun || '',
            profit_center: data.profit_center || '',
            nama_profit_center: data.nama_profit_center || '',
            keterangan: data.keterangan || '',
            debet: data.type == "debet" ? Number(data.total || 0) : 0,
            kredit: data.type == "kredit" ? Number(data.total || 0) : 0
        }

        setRows(prev => [...prev, newRow])
    }

    const onSave = (masterData: any) => {
        // prepare payload
        const payload = {
            ...masterData,
            details: rows
        }

        Swal.fire({
            title: 'Konfirmasi',
            html: 'Apakah anda ingin menyimpan data ini ?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#337ab7',
            cancelButtonColor: '#ffc107',
            confirmButtonText: 'Simpan'
        }).then(result => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Now loading',
                    allowEscapeKey: false,
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading()
                    }
                })

                const url = apiUrl.apiUrl + '/acc/penyesuaian/save'
                axios
                    .post(url, payload, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                    .then(response => {
                        if (response.data.success) {
                            Swal.fire({
                                title: 'Pemberitahuan',
                                html: response.data.message,
                                icon: 'success',
                                timer: 1000,
                                timerProgressBar: true,
                                willClose: () => {
                                    const id = response.data.data
                                    navigate('/acc/jurnal-memorial/detail/' + id)
                                }
                            })
                        } else {
                            showToast(response.data.message, 'error');
                            Swal.close()
                        }
                    })
                    .catch(error => {
                        showToast(error.message, 'error');
                    })
            }
        })
    }

    return (
        <Grid container spacing={6} sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Grid item xl={12} md={12} xs={12}>
                <Fab variant='extended' color='primary' sx={{ '& svg': { mr: 1 } }} onClick={() => window.history.back()}>
                    <Icon icon='mdi:arrow-left' />
                    Kembali
                </Fab>
                <div className={'content-block dx-card'} style={{ paddingRight: 20, paddingLeft: 20, paddingBottom: 10, paddingTop: 10 }}>
                    <Typography variant='h5' sx={{ textAlign: 'center' }}>
                        Buat Jurnal Memorial
                    </Typography>
                    <Divider />
                    <form onSubmit={handleSubmit(onSave)}>
                        <CardContent>
                            {/* Master Header */}
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <Controller
                                            name='sysdoctag_id'
                                            control={control}
                                            rules={{ required: true }}
                                            render={({ field: { value, onChange } }) => (
                                                <Autocomplete
                                                    value={valDoctag}
                                                    options={listDoctag}
                                                    id='sysdoctag_id'
                                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                                    onChange={handleChangeDoctag}
                                                    renderOption={(props, option) => {
                                                        return (
                                                            <li {...props} key={option.id}>
                                                                {option.name_tag}
                                                            </li>
                                                        )
                                                    }}
                                                    getOptionLabel={option => option.name_tag}
                                                    renderInput={params => (
                                                        <TextField
                                                            {...params}
                                                            label='Document'
                                                            error={Boolean(errors.sysdoctag_id)}
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    )}
                                                />
                                            )}
                                        />
                                        {errors.sysdoctag_id && (
                                            <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <Controller
                                            name='tgl'
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    label='Tanggal Transaksi'
                                                    type='date'
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={12} sx={{ mb: 3 }}>
                                    <FormControl fullWidth>
                                        <Controller
                                            name='keterangan'
                                            control={control}
                                            render={({ field }) => <TextField {...field} label='Keterangan' multiline rows={3} />}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 5 }} />

                            {/* Controls for details */}
                            <Stack direction='row' spacing={2} sx={{ mt: 5, mb: 2, justifyContent: 'flex-end' }}>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    startIcon={<Icon icon='mdi:plus' />}
                                    onClick={() => {
                                        setDialogType('debet')
                                        setOpenDialog(true)
                                    }}
                                >
                                    Tambah Debet
                                </Button>

                                <Button
                                    variant='contained'
                                    color='info'
                                    startIcon={<Icon icon='mdi:plus' />}
                                    onClick={() => {
                                        setDialogType('kredit')
                                        setOpenDialog(true)
                                    }}
                                >
                                    Tambah Kredit
                                </Button>
                            </Stack>

                            {/* Single Detail Table */}
                            <MsiDatagrid array={rows.map(r => ({
                                id: r.id,
                                // nomor_akun: r.nomor_akun,
                                nama_akun: r.nama_akun,
                                // profit_center: r.profit_center,
                                nama_profit_center: r.nama_profit_center,
                                keterangan: r.keterangan,
                                debet: r.debet,
                                kredit: r.kredit,
                                action: ''
                            }))}
                                hiddenColumns={['id']}
                                customRender={convertComponent}
                            />

                            {/* Totals & Balance */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                <Box>
                                    <Typography variant='subtitle1'>
                                        Total Debit: <b>{totalDebet.toLocaleString('id-ID')}</b>
                                    </Typography>
                                    <Typography variant='subtitle1'>
                                        Total Kredit: <b>{totalKredit.toLocaleString('id-ID')}</b>
                                    </Typography>
                                </Box>

                                <Box sx={{ textAlign: 'right' }}>
                                    {isBalance ? (
                                        <Box sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', px: 2, py: 1, borderRadius: 1 }}>
                                            <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
                                                Sudah Balance
                                            </Typography>
                                            <Typography variant='caption'>Debit = Kredit</Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ bgcolor: '#fff3e0', color: '#e65100', px: 2, py: 1, borderRadius: 1 }}>
                                            <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
                                                Tidak Balance
                                            </Typography>
                                            <Typography variant='caption'>
                                                Selisih: {Math.abs(totalDebet - totalKredit).toLocaleString('id-ID')}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            {/* Action buttons */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
                                <Button variant='contained' color='primary' type='submit' disabled={!isBalance}>
                                    SIMPAN
                                </Button>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    onClick={() => {
                                        // reset everything
                                        setRows([])
                                        setValue('sysdoctag_id', '')
                                        setValue('keterangan', '')
                                        setValue('tgl', moment().format('YYYY-MM-DD'))
                                    }}
                                >
                                    BATAL
                                </Button>
                            </Box>
                        </CardContent>
                    </form>
                </div>
            </Grid>

            {/* Reuse the same dialog, pass typeDefault */}
            <DialogAddRow
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                onSubmit={(d: any) => {
                    handleAddRow(d)
                    setOpenDialog(false)
                }}
                typeDefault={dialogType}
            />
        </Grid>
    )

    // helper: handler wrapper to add row (keadaan di closure)
    function handleAddRow2(data: any) {
        // create row and insert to state
        const newRow = {
            id: Math.random().toString(36).substr(2, 9),
            nomor_akun: data.nomor_akun,
            profit_center: data.profit_center,
            keterangan: data.keterangan,
            debet: data.type === 'debet' ? Number(data.total || 0) : 0,
            kredit: data.type === 'kredit' ? Number(data.total || 0) : 0
        }
        setRows(prev => [...prev, newRow])
    }
}

export default AddMemorialSingleTable